import { GENDER, TUserDetails } from '../users/users.interfaces';
import {
  ICreateStudentWithBasicDetailsRequest,
  IGuardianDetailsWireDto,
  IPatchStudentBasicDetailsRequest,
} from './students.interfaces';

/** Resolve institute scope: prefer env override, fall back to stored instituteId from login. */
export function getAdmissionInstituteId(): string {
  if (
    typeof process.env.REACT_APP_INSTITUTE_ID === 'string' &&
    process.env.REACT_APP_INSTITUTE_ID.trim() !== ''
  ) {
    return process.env.REACT_APP_INSTITUTE_ID;
  }
  return localStorage.getItem('instituteId') || '';
}

export function mapStep1StudentToBasicDetailsRequest(params: {
  student: Pick<
    TUserDetails,
    'basicDetails' | 'contactDetails'
  >;
  createdBy: string;
  profilePictureRef?: Record<string, unknown>;
}): ICreateStudentWithBasicDetailsRequest {
  const { student, createdBy, profilePictureRef } = params;
  const g = student.basicDetails?.gender;
  const gender =
    g === 'select' || g === undefined ? '' : String(g);

  return {
    firstName: student.basicDetails?.firstName ?? '',
    middleName: student.basicDetails?.middleName ?? '',
    lastName: student.basicDetails?.lastName ?? '',
    gender,
    dateOfBirth: student.basicDetails?.dateOfBirth ?? '',
    countryCode: student.contactDetails?.phone?.countryCode ?? '',
    phoneNumber: student.contactDetails?.phone?.phoneNumber ?? '',
    email: student.contactDetails?.email?.email ?? '',
    addressDescription: student.contactDetails?.address?.address1 ?? '',
    city: student.contactDetails?.address?.city ?? '',
    state: student.contactDetails?.address?.state ?? '',
    country: student.contactDetails?.address?.country ?? '',
    pinCode: student.contactDetails?.address?.postalCode ?? '',
    profilePictureRef: profilePictureRef ?? {},
    createdBy,
  };
}

/**
 * Edit-mode body for PATCH /students/basic-details. Same flat field mapping as
 * the create, but keyed by `studentId` and without `createdBy`/`profilePictureRef`
 * (the actor comes from the JWT). Sends the full editable set the form currently
 * holds — Step 1 validation gates Continue, so required fields are non-empty.
 */
export function mapStep1StudentToPatchBasicDetails(params: {
  studentId: string;
  userId?: string;
  student: Pick<TUserDetails, 'basicDetails' | 'contactDetails'>;
}): IPatchStudentBasicDetailsRequest {
  const { studentId, userId, student } = params;
  const g = student.basicDetails?.gender;
  const gender = g === 'select' || g === undefined ? '' : String(g);

  return {
    studentId,
    ...(userId ? { userId } : {}),
    firstName: student.basicDetails?.firstName ?? '',
    middleName: student.basicDetails?.middleName ?? '',
    lastName: student.basicDetails?.lastName ?? '',
    gender,
    dateOfBirth: student.basicDetails?.dateOfBirth ?? '',
    countryCode: student.contactDetails?.phone?.countryCode ?? '',
    phoneNumber: student.contactDetails?.phone?.phoneNumber ?? '',
    email: student.contactDetails?.email?.email ?? '',
    addressDescription: student.contactDetails?.address?.address1 ?? '',
    city: student.contactDetails?.address?.city ?? '',
    state: student.contactDetails?.address?.state ?? '',
    country: student.contactDetails?.address?.country ?? '',
    pinCode: student.contactDetails?.address?.postalCode ?? '',
  };
}

function mapOneGuardianToWire(
  guardian: Partial<TUserDetails>,
  opts: { relationship: string; isPrimary: boolean }
): IGuardianDetailsWireDto {
  const addr = guardian.contactDetails?.address;
  return {
    userId: guardian.id ?? '',
    basicDetails: {
      firstName: guardian.basicDetails?.firstName ?? '',
      middleName: guardian.basicDetails?.middleName ?? '',
      lastName: guardian.basicDetails?.lastName ?? '',
      gender:
        guardian.basicDetails?.gender === GENDER.MALE ||
        guardian.basicDetails?.gender === GENDER.FEMALE ||
        guardian.basicDetails?.gender === GENDER.OTHER
          ? String(guardian.basicDetails.gender)
          : '',
    },
    contactDetails: {
      phone: {
        countryCode: guardian.contactDetails?.phone?.countryCode ?? '',
        phoneNumber: guardian.contactDetails?.phone?.phoneNumber ?? '',
        isVerified: guardian.contactDetails?.phone?.isVerified ?? false,
      },
      email: guardian.contactDetails?.email?.email ?? '',
      address: {
        street: addr?.address1 ?? '',
        city: addr?.city ?? '',
        state: addr?.state ?? '',
        zipCode: addr?.postalCode ?? '',
        country: addr?.country ?? '',
      },
    },
    relationship: opts.relationship,
    isPrimary: opts.isPrimary,
    isEmergencyContact: opts.isPrimary,
  };
}

export function mapGuardianFormToGuardianDetailsPayload(params: {
  guardian1: Partial<TUserDetails>;
  guardian2?: Partial<TUserDetails>;
}): {
  guardianDetails1: IGuardianDetailsWireDto;
  guardianDetails2?: IGuardianDetailsWireDto;
} {
  const g1 = mapOneGuardianToWire(params.guardian1, {
    relationship: 'guardian',
    isPrimary: true,
  });
  const g2Raw = params.guardian2;
  const hasSecond =
    g2Raw &&
    (String(g2Raw.basicDetails?.firstName ?? '').trim() !== '' ||
      String(g2Raw.basicDetails?.lastName ?? '').trim() !== '');
  // NOTE: when guardian 2's name is blank we omit guardianDetails2 entirely.
  // The PUT /students/guardian-details endpoint is an upsert that NEVER deletes,
  // so omitting it leaves any existing second guardian untouched — clearing the
  // guardian 2 fields does NOT remove that guardian. There is no remove-guardian
  // operation on this endpoint yet; a dedicated backend path is needed for that.
  return {
    guardianDetails1: g1,
    ...(hasSecond
      ? {
          guardianDetails2: mapOneGuardianToWire(g2Raw, {
            relationship: 'guardian',
            isPrimary: false,
          }),
        }
      : {}),
  };
}
